import { Membre, Relation, TreeNode } from '../types';

const HIERARCHICAL_RELATIONS = ['parent', 'grand_parent', 'arriere_grand_parent'];
const CONJUGAL_RELATIONS = ['conjoint', 'ex_conjoint'];
const SIBLING_RELATIONS = ['frere', 'soeur', 'demi_frere', 'demi_soeur'];

function createCoupleNode(
  person1: TreeNode,
  person2: TreeNode,
  rel?: { type: string; dateDebut?: string; dateFin?: string },
): TreeNode {
  return {
    id: `couple-${person1.id}-${person2.id}`,
    name: `${person1.name} & ${person2.name}`,
    attributes: {
      sexe: 'couple',
      isCouple: true,
      isDivorced: rel?.type === 'ex_conjoint',
      marriageDate: rel?.dateDebut,
      divorceDate: rel?.dateFin,
      person1,
      person2,
      hasSpouse: false,
      spouseName: '',
      hasSiblings: false,
      siblingsCount: 0,
      siblingsNames: '',
    },
    children: [],
  };
}

export function buildTree(membres: Membre[], relations: Relation[]): TreeNode[] {
  if (membres.length === 0) return [];

  const membresMap: Record<number, TreeNode> = Object.fromEntries(
    membres.map(m => [m.id, {
      id: m.id,
      name: `${m.prenom} ${m.nom}`,
      attributes: {
        sexe: m.sexe,
        prenom_naissance: m.prenom_naissance,
        prenom_naissance_accord: m.prenom_naissance_accord,
        date_naissance: m.date_naissance,
        date_deces: m.date_deces,
        statut_marital: m.statut_marital,
        membreId: m.id,
        photo: m.photo,
        lieu_naissance: m.lieu_naissance,
        lieu_deces: m.lieu_deces,
        hasSpouse: false,
        spouseName: '',
        hasSiblings: false,
        siblingsCount: 0,
        siblingsNames: '',
      },
      children: [],
    }])
  );

  const spouseRelations = new Map<number, { id: number; name: string; type: string; dateDebut?: string; dateFin?: string }>();
  const siblingsMap = new Map<number, Array<{ id: number; name: string; type: string }>>();
  const processedCouples = new Set<string>();
  const parentChildRelations = new Map<number, Set<number>>();

  relations.forEach(rel => {
    const source = membresMap[rel.membre_source];
    const target = membresMap[rel.membre_cible];
    if (!source || !target) return;

    if (HIERARCHICAL_RELATIONS.includes(rel.type_relation)) {
      const parentId = Number(source.id);
      const childId = Number(target.id);
      if (!parentChildRelations.has(parentId)) parentChildRelations.set(parentId, new Set());
      parentChildRelations.get(parentId)!.add(childId);
    } else if (CONJUGAL_RELATIONS.includes(rel.type_relation)) {
      const sourceId = Number(source.id);
      const targetId = Number(target.id);
      spouseRelations.set(sourceId, { id: targetId, name: target.name, type: rel.type_relation, dateDebut: rel.date_debut, dateFin: rel.date_fin });
      spouseRelations.set(targetId, { id: sourceId, name: source.name, type: rel.type_relation, dateDebut: rel.date_debut, dateFin: rel.date_fin });
    } else if (SIBLING_RELATIONS.includes(rel.type_relation)) {
      const sourceId = Number(source.id);
      const targetId = Number(target.id);
      if (!siblingsMap.has(sourceId)) siblingsMap.set(sourceId, []);
      if (!siblingsMap.has(targetId)) siblingsMap.set(targetId, []);
      siblingsMap.get(sourceId)!.push({ id: targetId, name: target.name, type: rel.type_relation });
      siblingsMap.get(targetId)!.push({ id: sourceId, name: source.name, type: rel.type_relation });
    }
  });

  Object.values(membresMap).forEach(member => {
    const memberId = Number(member.id);
    if (spouseRelations.has(memberId)) {
      const spouse = spouseRelations.get(memberId)!;
      member.attributes!.hasSpouse = true;
      member.attributes!.spouseName = spouse.name;
      member.attributes!.spouseId = spouse.id;
    }
    if (siblingsMap.has(memberId)) {
      const siblings = siblingsMap.get(memberId)!;
      member.attributes!.hasSiblings = true;
      member.attributes!.siblingsCount = siblings.length;
      member.attributes!.siblingsNames = siblings.slice(0, 2).map(s => s.name).join(', ');
      if (siblings.length > 2) member.attributes!.siblingsNames += ` +${siblings.length - 2}`;
    }
  });

  const coupleNodes = new Map<string, TreeNode>();

  Object.values(membresMap).forEach(member => {
    const memberId = Number(member.id);
    if (member.attributes?.hasSpouse && member.attributes.spouseId) {
      const spouseId = member.attributes.spouseId;
      const spouse = membresMap[spouseId];
      if (!spouse) return;
      const coupleKey1 = `${memberId}-${spouseId}`;
      const coupleKey2 = `${spouseId}-${memberId}`;
      if (!processedCouples.has(coupleKey1) && !processedCouples.has(coupleKey2)) {
        const memberChildren = parentChildRelations.get(memberId) || new Set<number>();
        const spouseChildren = parentChildRelations.get(spouseId) || new Set<number>();
        const allCoupleChildren = new Set([...Array.from(memberChildren), ...Array.from(spouseChildren)]);
        const coupleNode = createCoupleNode(member, spouse, spouseRelations.get(memberId));
        if (allCoupleChildren.size > 0) {
          coupleNode.children = Array.from(allCoupleChildren).map(cid => membresMap[cid]);
          allCoupleChildren.forEach(cid => { memberChildren.delete(cid); spouseChildren.delete(cid); });
        }
        coupleNodes.set(coupleKey1, coupleNode);
        processedCouples.add(coupleKey1);
        processedCouples.add(coupleKey2);
      }
    }
  });

  // Map each member involved in a couple to their couple node.
  // Iteration order matters: the last write wins, which mirrors the processedCouples
  // logic (first couple created for a member is the canonical one).
  // We reverse the iteration so the first-created couple takes priority.
  const coupleByMemberId = new Map<number, TreeNode>();
  Array.from(coupleNodes.values()).reverse().forEach(cn => {
    const p1Id = Number(cn.attributes!.person1!.id);
    const p2Id = Number(cn.attributes!.person2!.id);
    coupleByMemberId.set(p1Id, cn);
    coupleByMemberId.set(p2Id, cn);
  });

  // Track couple nodes that are used as children (they should not appear as roots).
  const usedCoupleIds = new Set<string>();

  // Returns a shallow copy of a couple node annotated with which member is the
  // actual child (so the layout can draw the edge to the correct card).
  const withChildSide = (coupleNode: TreeNode, childMemberId: number): TreeNode => ({
    ...coupleNode,
    attributes: { ...coupleNode.attributes, childPersonId: childMemberId },
  });

  // Substitute individual child nodes with their couple node where applicable.
  // Process in reverse insertion order (youngest generations first) so that by
  // the time a parent couple is processed, its children's couple nodes already
  // have their own children correctly set — avoiding stale shallow-copy references.
  Array.from(coupleNodes.values()).reverse().forEach(cn => {
    if (cn.children?.length) {
      cn.children = cn.children.map(child => {
        const childId = Number(child.id);
        const childCouple = coupleByMemberId.get(childId);
        if (childCouple && childCouple !== cn) {
          usedCoupleIds.add(childCouple.id as string);
          return withChildSide(childCouple, childId);
        }
        return child;
      });
    }
  });

  // Apply remaining parent-child relations for individual (non-coupled) parents,
  // also substituting couple nodes where children are part of a couple.
  parentChildRelations.forEach((childIds, parentId) => {
    const parent = membresMap[parentId];
    if (parent && childIds.size > 0) {
      parent.children = Array.from(childIds).map(cid => {
        const childCouple = coupleByMemberId.get(cid);
        if (childCouple) {
          usedCoupleIds.add(childCouple.id as string);
          return withChildSide(childCouple, cid);
        }
        return membresMap[cid];
      });
    }
  });

  const enfants = new Set(
    relations.filter(r => HIERARCHICAL_RELATIONS.includes(r.type_relation)).map(r => r.membre_cible)
  );
  const racines = membres.filter(m => !enfants.has(m.id));

  return [
    ...racines.map(r => membresMap[r.id]).filter(node => {
      const nodeId = Number(node.id);
      const spouseId = node.attributes?.spouseId || 0;
      return !processedCouples.has(`${nodeId}-${spouseId}`) && !processedCouples.has(`${spouseId}-${nodeId}`);
    }),
    ...Array.from(coupleNodes.values()).filter(cn => !usedCoupleIds.has(cn.id as string)),
  ];
}
